import { useState, useEffect } from "react";
import { FaStream, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import ActionDropdown from "../ActionDropdown";
import API from "../../api";
import { BASE_URL } from "../../config";
import { showConfirm, showError, showSuccess } from "../../utils/dialog";
import { formatAppDateTime } from "../../utils/appDateTime";

const formatFileSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "-";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(2)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const normalizeMessageBodyLinks = (html = "") => {
  if (!html || typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html || "";
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("a[href]").forEach((anchor) => {
    const href = anchor.getAttribute("href") || "";
    const personnelPathIndex = href.indexOf("/personnel?");

    if (personnelPathIndex >= 0) {
      anchor.setAttribute("href", `${window.location.origin}${href.slice(personnelPathIndex)}`);
      return;
    }

    const lastUrl = href
      .split(",")
      .map((part) => part.trim())
      .filter((part) => /^https?:\/\//i.test(part))
      .pop();

    if (!lastUrl) return;
    try {
      const parsed = new URL(lastUrl);
      if (parsed.pathname === "/personnel") {
        anchor.setAttribute(
          "href",
          `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`,
        );
      }
    } catch {
      // Leave unparseable links as-is.
    }
  });

  return doc.body.innerHTML;
};

const MessageQueueForm = ({ onClose, message }) => {
  const [selectedTab, setSelectedTab] = useState("OVERVIEW");
  const [selectedItems, setSelectedItems] = useState([]);
  const [emailContent] = useState(normalizeMessageBodyLinks(message?.body || ""));
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);

  useEffect(() => {
    if (selectedTab !== "ATTACHMENTS" || !message?.message_queue_id) return;
    let cancelled = false;
    setAttachmentsLoading(true);
    API.get(`/api/admin/messages/queue/${message.message_queue_id}/attachments`)
      .then((res) => {
        if (cancelled) return;
        setAttachments(Array.isArray(res?.data?.data) ? res.data.data : []);
      })
      .catch((err) => {
        if (cancelled) return;
        setAttachments([]);
        showError(err?.response?.data?.message || "Failed to load attachments");
      })
      .finally(() => {
        if (!cancelled) setAttachmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTab, message?.message_queue_id]);

  const handleDownloadAttachment = (attachment) => {
    if (!message?.message_queue_id || !attachment?.message_attachment_id) return;
    window.open(
      `${BASE_URL}/api/admin/messages/queue/${message.message_queue_id}/attachments/${attachment.message_attachment_id}/download`,
      "_blank",
    );
  };
  const recipientDetails = Array.isArray(message?.recipient_details) && message.recipient_details.length > 0
    ? message.recipient_details
    : (typeof message?.recipients === "string" && message.recipients.trim()
      ? message.recipients
          .split(",")
          .map((r) => ({ recipient: r.trim(), state: message?.state, sent_date: message?.sent_date || message?.create_date }))
          .filter((r) => r.recipient)
      : []);

  const handleResend = async () => {
    if (!message?.message_queue_id) return showError("Message queue id not found");
    const ok = await showConfirm("Are you sure you want to resend this message?");
    if (!ok) return;

    try {
      const res = await API.post("/api/admin/messages/queue/bulk-action", {
        action: "resend",
        messageQueueIds: [message.message_queue_id],
      });
      showSuccess(res?.data?.message || "Message resent successfully");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to resend message");
    }
  };

  const handleForward = async () => {
    const to = window.prompt("Forward to email address:");
    if (!to || !String(to).trim()) return;

    try {
      const subject = message?.message_name || "Forwarded Message";
      const body = emailContent || normalizeMessageBodyLinks(message?.body || "") || subject;
      const res = await API.post("/api/admin/messages/send", {
        to: String(to).trim(),
        subject,
        body,
      });
      showSuccess(res?.data?.message || "Message forwarded successfully");
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to forward message");
    }
  };

  const handleDone = async () => {
    if (!message?.message_queue_id) {
      onClose?.();
      return;
    }
    try {
      await API.post("/api/admin/messages/queue/bulk-action", {
        action: "sent",
        messageQueueIds: [message.message_queue_id],
      });
      onClose?.(true);
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to mark message as sent");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-[95%] sm:w-full max-w-4xl p-6 relative flex flex-col max-h-[90vh] overflow-y-auto overflow-x-hidden">
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
          >
          ✖
        </button>
  
        {/* Header */}
        <div className="flex">
          <FaStream className="text-buttonRed text-lg w-5 h-5 mt-1 mr-2"/>
          <h2 className="text-xl text-formTitleText font-semibold mb-2">Message Queue</h2>
        </div>

        <div className="h-[1px] bg-[#DC1F004D]"></div>

        <div className="flex space-x-8 mb-2 mt-4 border-b border-border">
          {["OVERVIEW", "LOGS", "ATTACHMENTS", "EVENTS"].map(tab => (
              <button
              key={tab}
              onClick={() => {
                  setSelectedTab(tab);
                  setSelectedItems([]); // Clear selections when changing tabs
              }}
              className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors ${
                  selectedTab === tab
                  ? "border-[#DC1F00] text-sidebar-active"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              >
              {tab}
              </button>
          ))}
        </div>

        {selectedTab === "OVERVIEW" && (
          <div className="flex-1 overflow-y-auto p-2">
            <div className="flex items-start justify-between gap-4">
              <div className="">
                <label className="block text-sm text-textGray mb-1">To Recipients</label>
                <div className="space-y-1">
                  {recipientDetails.length === 0 ? (
                    <span>-</span>
                  ) : recipientDetails.map((r, i) => (
                    <div key={`${r.recipient}-${i}`} className="flex items-center gap-1">
                      <span className="mr-1">{r.recipient}</span>
                      {r.state === "S" ? (
                        <FaCheckCircle size={15} className="text-green-500" />
                      ) : r.state === "F" || r.state === "I" ? (
                        <FaExclamationCircle size={15} className="text-red-500" />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm text-textGray mb-1">Date</label>
                <span>{formatAppDateTime(message?.create_date, { emptyValue: "-" })}</span>
              </div>

              <div>
                <label className="block text-sm text-textGray mb-1">Status</label>
                <span>{message?.state || "-"}</span>
              </div>

              <div>
                <label className="block text-sm text-textGray mb-1">Message Type</label>
                <span>{message?.message_name || "-"}</span>
              </div>
            </div>

            <label className="block text-sm text-textGray mb-2 mt-3">Subject</label>
            <span>{message?.message_name || "-"}</span>

            <label className="block text-sm text-textGray mb-2 mt-3">Body</label>
            <div className="w-full h-64 p-3 border border-gray-300 rounded-lg bg-gray-50 overflow-y-auto">
              <div dangerouslySetInnerHTML={{ __html: emailContent }} />
            </div>
          </div>
        )}

        {selectedTab === "LOGS" && (
          <div className="flex-1 overflow-y-auto min-h-[150px] p-4">
            <table className="w-full border-collapse mb-2">
              <thead className="border-b border-gray-400">
                <tr className="text-left text-sm text-textGray">
                  <th className="pb-2 w-[70%]">Message</th>
                  <th className="pb-2 w-[20%]">Date</th>
                </tr>
              </thead>
              <tbody>
                {recipientDetails.length === 0 ? (
                  <tr className="border-b border-gray-400">
                    <td className="py-2 text-sm text-gray-500">No recipient logs available</td>
                    <td className="py-2">-</td>
                  </tr>
                ) : recipientDetails.map((r, idx) => {
                  const stateLabel =
                    r.state === "S" ? "Sent"
                      : r.state === "F" ? "Failed"
                      : r.state === "I" ? "Invalid"
                      : r.state === "Q" ? "Queued"
                      : r.state || "Processed";
                  const when = r.sent_date || r.create_date || message?.sent_date || message?.create_date;
                  return (
                    <tr key={`${r.recipient}-${idx}`} className="border-b border-gray-400">
                      <td className="py-2">{`${stateLabel} to ${r.recipient}`}</td>
                      <td className="py-2">{formatAppDateTime(when, { emptyValue: "-" })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {selectedTab === "ATTACHMENTS" && (
          <div className="flex-1 overflow-y-auto min-h-[150px] p-4">
            <table className="w-full border-collapse mb-2">
              <thead className="border-b border-gray-400">
                <tr className="text-left text-sm text-textGray">
                  <th className="pb-2 w-[70%]">File Name</th>
                  <th className="pb-2 w-[20%]">File Size</th>
                  <th className="pb-2 w-[2%]"></th>
                </tr>
              </thead>
              <tbody>
                {attachmentsLoading ? (
                  <tr className="border-b border-gray-400">
                    <td className="py-2 text-sm text-gray-500" colSpan={3}>Loading attachments…</td>
                  </tr>
                ) : attachments.length === 0 ? (
                  <tr className="border-b border-gray-400">
                    <td className="py-2 text-sm text-gray-500" colSpan={3}>No attachments</td>
                  </tr>
                ) : attachments.map((att) => (
                  <tr key={att.message_attachment_id} className="border-b border-gray-400">
                    <td className=" py-2">{att.filename || "-"}</td>
                    <td className=" py-2">{formatFileSize(att.size)}</td>
                    <td className=" py-2">
                      <ActionDropdown
                        actions={[
                          { label: "Download", onClick: () => handleDownloadAttachment(att) },
                        ]}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {selectedTab === "EVENTS" && (
          <div className="flex-1 overflow-y-auto min-h-[150px] p-4">
            <table className="w-full border-collapse mb-2">
              <thead className="border-b border-gray-400">
                <tr className="text-left text-sm text-textGray">
                  <th className="pb-2 w-[60%]">Recipient</th>
                  <th className="pb-2 w-[20%]">Event</th>
                  <th className="pb-2 w-[20%]">Date</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-400">
                  <td className=" py-2">Cjosuala@gmail.com</td>
                  <td className=" py-2">Open</td>
                  <td className=" py-2">Nov 9 10:50 am</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {(selectedTab === "OVERVIEW" || selectedTab === "LOGS" || selectedTab === "ATTACHMENTS" || selectedTab === "EVENTS")&& (
        <div className="flex items-center justify-start gap-4 mt-2">
          <button
            type="submit"
            onClick={handleDone}
            className="w-[20%] bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
          >
          DONE
          </button>
          <button
            onClick={handleResend}
            className="w-[20%] border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
          >
          RESEND
          </button>
          <button
            onClick={handleForward}
            className="w-[20%] border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
          >
          FORWARD
          </button>
        </div>
        )}
        
      </div>
    </div>
  )
};

export default MessageQueueForm;

